import { useState, useCallback, useMemo, useEffect } from 'react'
import { ILvl4Item, ILvl1Item } from 'pages/NewProject/components/ProjectSkillsStandards/interfaces'
import { ISkillsStandards } from 'pages/NewProject/components/ProjectSkillsStandards/interfaces'
import { SelectedSkillStandard } from '../../../../types'

export interface GradeOption { id: string; name: string; sequence: number }

export type FilterType = 'skill' | 'content' | null

const extractCategoryNames = (items: ILvl1Item[]): string[] =>
  items.map(item => item.name).filter(Boolean)

const getCategoriesForType = (
  subjects: ILvl1Item[],
  axes: ILvl1Item[],
  type: FilterType,
): string[] => {
  if (type === 'skill') return extractCategoryNames(axes).sort()
  if (type === 'content') return extractCategoryNames(subjects).sort()
  return [...new Set([...extractCategoryNames(axes), ...extractCategoryNames(subjects)])].sort()
}

export const useModalState = (
  selectedItems: SelectedSkillStandard[],
  results: ILvl4Item[],
  isSearching: boolean,
  skillsStandards: ISkillsStandards | null,
) => {
  const [pendingItems, setPendingItems] = useState<ILvl4Item[]>([])
  const [filterType, setFilterType] = useState<FilterType>(null)
  const [filterCategories, setFilterCategories] = useState<string[]>([])
  const [filterGrades, setFilterGrades] = useState<string[]>([])
  const [showResults, setShowResults] = useState(false)

  const { subjects, axes } = useMemo(() => ({
    subjects: skillsStandards?.curriculumItems?.subjects || [],
    axes: skillsStandards?.curriculumItems?.axes || [],
  }), [skillsStandards])

  const hasFilters = useMemo(
    () => filterType !== null || filterCategories.length > 0 || filterGrades.length > 0,
    [filterType, filterCategories, filterGrades],
  )

  const hasSearchResults = results.length > 0
  const hasPendingItems = pendingItems.length > 0

  useEffect(() => {
    if ((hasSearchResults || hasPendingItems) && !isSearching) {
      const timer = setTimeout(() => setShowResults(true), 50)
      return () => clearTimeout(timer)
    }
    if (!hasSearchResults && !hasPendingItems) setShowResults(false)
  }, [hasSearchResults, hasPendingItems, isSearching])

  const alreadyAddedIds = useMemo(
    () => new Set(selectedItems.map(i => i.id)),
    [selectedItems],
  )

  const pendingIds = useMemo(
    () => new Set(pendingItems.map(i => i.id)),
    [pendingItems],
  )

  const availableCategories = useMemo(
    () => getCategoriesForType(subjects, axes, filterType),
    [subjects, axes, filterType],
  )

  const availableGrades = useMemo((): GradeOption[] => {
    const map = new Map<string, GradeOption>()
    for (const item of results) {
      for (const g of item.grades || []) {
        if (g.id && !map.has(g.id)) {
          map.set(g.id, { id: g.id, name: g.name, sequence: g.sequence })
        }
      }
    }
    return Array.from(map.values())
  }, [results])

  useEffect(() => {
    if (results.length === 0 && filterGrades.length > 0) {
      setFilterGrades([])
    }
  }, [results.length, filterGrades.length])

  const filteredResults = useMemo(() => {
    let res = results

    if (filterType) {
      res = res.filter(i => i.type === filterType)
    }

    if (filterCategories.length > 0) {
      res = res.filter(i => i.lv1Name && filterCategories.includes(i.lv1Name))
    }

    if (filterGrades.length > 0) {
      res = res.filter(i =>
        (i.grades || []).some((g: { id: string }) => filterGrades.includes(g.id)),
      )
    }

    const otherResults = res.filter(item => !pendingIds.has(item.id))
    return [...pendingItems, ...otherResults]
  }, [results, filterType, filterCategories, filterGrades, pendingItems, pendingIds])

  const handleFilterTypeChange = useCallback((newType: FilterType) => {
    setFilterType(newType)

    if (newType && filterCategories.length > 0) {
      const validCategories = new Set(getCategoriesForType(subjects, axes, newType))
      const filtered = filterCategories.filter(cat => validCategories.has(cat))
      if (filtered.length !== filterCategories.length) {
        setFilterCategories(filtered)
      }
    }
  }, [subjects, axes, filterCategories])

  const handleToggleItem = useCallback((item: ILvl4Item) => {
    setPendingItems(prev => {
      const exists = prev.some(p => p.id === item.id)
      return exists ? prev.filter(p => p.id !== item.id) : [...prev, item]
    })
  }, [])

  const reset = useCallback(() => {
    setPendingItems([])
    setFilterType(null)
    setFilterCategories([])
    setFilterGrades([])
    setShowResults(false)
  }, [])

  const clearFilters = useCallback(() => {
    setFilterType(null)
    setFilterCategories([])
    setFilterGrades([])
  }, [])

  return useMemo(() => ({
    pendingItems,
    setPendingItems,
    filterType,
    handleFilterTypeChange,
    filterCategories,
    setFilterCategories,
    filterGrades,
    setFilterGrades,
    showResults,
    alreadyAddedIds,
    pendingIds,
    availableCategories,
    availableGrades,
    filteredResults,
    handleToggleItem,
    reset,
    clearFilters,
    hasFilters,
  }), [
    pendingItems,
    filterType,
    handleFilterTypeChange,
    filterCategories,
    filterGrades,
    showResults,
    alreadyAddedIds,
    pendingIds,
    availableCategories,
    availableGrades,
    filteredResults,
    handleToggleItem,
    reset,
    clearFilters,
    hasFilters,
  ])
}
